// src/data/policyClient.js
//
// ADMIN-POLICY-EDITOR: Client for the Space Policy admin API.
//
//   GET    /api/admin/policy                — baseline + overrides + locations/regions/markets
//   PUT    /api/admin/policy/baseline       — upsert baseline knobs
//   PUT    /api/admin/policy/offices/:id    — upsert per-office override
//   DELETE /api/admin/policy/offices/:id    — clear per-office override
//   POST   /api/admin/policy/bulk-apply     — apply knobs to a set of offices

import {
  AuthError,
  NetworkError,
  ValidationError,
  errorFromResponse,
} from "./errors.js";
import { log } from "../utils/logger.js";

async function jsonRequest(url, init = {}) {
  let res;
  try {
    res = await fetch(url, {
      ...init,
      credentials: "include",
      headers: { "Content-Type": "application/json", ...(init.headers || {}) },
    });
  } catch (err) {
    log.error("policyClient: network failure", { url, err: String(err) });
    throw new NetworkError(`Failed to reach ${url}`, err);
  }
  if (!res.ok) {
    const e = await errorFromResponse(res);
    log.warn("policyClient: non-2xx", { url, status: res.status, name: e.name });
    throw e;
  }
  if (res.status === 204) return null;
  try {
    return await res.json();
  } catch (err) {
    throw new NetworkError(`Malformed JSON from ${url}`, err);
  }
}

/**
 * Fetch baseline policy, overrides, and location/region/market lists.
 * @returns {Promise<{ baseline, overrides, locations, regions, markets }>}
 */
export async function getPolicy() {
  return jsonRequest("/api/admin/policy");
}

/**
 * Upsert the company-wide baseline.
 * @param {{ knobs: object, effective_from: string }} payload
 * @returns {Promise<{ policy: object }>}
 */
export async function putPolicyBaseline(payload) {
  return jsonRequest("/api/admin/policy/baseline", {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function previewPolicyBaseline(payload) {
  return jsonRequest("/api/admin/policy/baseline/preview", {
    method: "POST", body: JSON.stringify(payload),
  });
}

/**
 * Upsert a per-office override.
 * @param {string} locationId
 * @param {{ knobs: object, effective_from: string }} payload
 * @returns {Promise<{ policy: object }>}
 */
export async function putPolicyOverride(locationId, payload) {
  return jsonRequest(`/api/admin/policy/offices/${encodeURIComponent(locationId)}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

/**
 * Clear (hard-delete) the per-office override for a location.
 * @param {string} locationId
 * @returns {Promise<null>}
 */
export async function deletePolicyOverride(locationId) {
  return jsonRequest(`/api/admin/policy/offices/${encodeURIComponent(locationId)}`, {
    method: "DELETE",
  });
}

/**
 * Bulk-apply knobs to a set of offices.
 * @param {{ locationIds: string[], knobs: object, effective_from: string }} payload
 * @returns {Promise<{ applied: number, locationIds: string[] }>}
 */
export async function bulkApplyPolicy(payload) {
  return jsonRequest("/api/admin/policy/bulk-apply", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}
